import React, { useEffect, useMemo, useRef, useState } from "react";
import type { AxiosError } from "axios";
import { useQuery } from "@tanstack/react-query";
import { Autocomplete, Skeleton, TextField } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import BaseApiService from "../api/BaseApiService";

interface BasicEntity {
  id: number;
  name: string;
}

type RemoteFilter = Record<string, null | string | number | [string, number]>;

type ManyToOneSelectProps<T extends BasicEntity> = {
  entity: string;
  version?: string; // build number (doesn't change during runtime)

  // New preferred controlled inputs
  value?: T | null;
  valueId?: number;

  /**
   * New preferred unified change callback.
   * Fires for user changes and for programmatic autocorrect/autoselect changes triggered by this component.
   * The source lets callers save only deliberate user selections.
   */
  onChange?: (value: T | null, id: number, source: "user" | "automatic") => void;

  // Legacy (still supported)
  /**
   * @deprecated Use `valueId` and `onChange` instead.
   */
  currentId?: number; // 0 means "none"

  /**
   * @deprecated Use `onChange` instead.
   */
  setId?: (newId: number) => any;

  /**
   * @deprecated Use `value` and `onChange` instead.
   */
  currentObject?: T | null;

  /**
   * @deprecated Use `onChange` instead.
   */
  setObject?: (obj: T | null) => void;

  // filtering
  filter?: (x: T) => boolean;
  filterReady?: boolean;

  remoteFilter?: RemoteFilter;
  remoteFilterReady?: boolean;

  // rendering/UX
  wrapper?: (c: React.ReactElement) => React.ReactElement;

  favorites?: number[];
  favoritesTitle?: string;

  placeholder?: string;
  label?: string;

  sx?: SxProps<Theme>;
  autoSelect?: boolean;
  fullWidth?: boolean;

  disabled?: boolean;
  size?: "medium" | "small";

  displayFunction?: (x: T | undefined) => string;

  /**
   * If true (default), autocomplete options are shown immediately (current behavior).
   * If false, options are only shown after the user typed at least 3 characters.
   */
  showAllOptions?: boolean;

  /**
   * Historical behavior:
   * - false: sideEffect runs once on initial ready AND on every later semantic change
   * - true: sideEffect does NOT run on initial ready, but DOES run on every later semantic change
   */
  sideEffectOnlyOnChange?: boolean;

  injectElements?: T[];

  /**
   * Legacy helper; effectively superseded by `onChange` now.
   * Still supported for backward compatibility.
   */
  sideEffect?: (selectedObject: T | null) => any;
  identityProp?: string;
  clearIcon?: React.ReactNode;
  showWithoutChoice?: boolean;
  queryKeyOverride?: string[];
  queryFn?: () => Promise<any>;
};

export default function ManyToOneSelect<T extends BasicEntity>({
  entity,
  version = undefined,
  identityProp = "id",

  // new preferred
  value,
  valueId,
  onChange,

  // legacy
  currentId,
  setId,
  currentObject,
  setObject,

  filter,
  filterReady = true,

  remoteFilter,
  remoteFilterReady = true,

  wrapper = (t) => t,

  favorites = [],
  favoritesTitle = "",

  placeholder = "",
  label = "",
  queryFn = undefined,
  sx,
  autoSelect = false,
  fullWidth = true,
  queryKeyOverride = [],

  disabled = false,
  size = "medium",
  injectElements = [],

  displayFunction = (o) =>
    !!o
      ? o.name + ("firstName" in (o as any) ? ", " + (o as any).firstName : "") + ("number" in (o as any) ? " (" + (o as any).number + ")" : "")
      : "",

  showAllOptions = true,

  sideEffectOnlyOnChange = false,
  sideEffect,

  clearIcon,
  showWithoutChoice = false,
}: ManyToOneSelectProps<T>) {
  // Normalize current value: prefer new props, fallback to legacy
  const effectiveObjectProp: T | null | undefined = value !== undefined ? value : currentObject;
  const effectiveIdProp: number | undefined = valueId !== undefined ? valueId : currentId;

  // Track user input for gating options
  const [inputValue, setInputValue] = useState("");
  const showOptions = showAllOptions || inputValue.trim().length >= 3;

  const queryKey = useMemo(() => {
    if (queryKeyOverride && queryKeyOverride.length > 0) return queryKeyOverride;
    if (!remoteFilter) return ["get", entity, "list"] as const;
    return ["get", entity, "list", JSON.stringify(remoteFilter)] as const;
  }, [entity, remoteFilter, queryKeyOverride]);

  const query = useQuery<T[], AxiosError>({
    queryKey,
    enabled: remoteFilter || !!queryFn ? remoteFilterReady : true,
    queryFn: queryFn
      ? queryFn
      : remoteFilter
        ? BaseApiService.getEntitiesWithFilter(entity, remoteFilter, version)
        : BaseApiService.getEntities(entity, undefined, version),
  });

  const filteredOptions: T[] = useMemo(() => {
    if (!query.isSuccess || !filterReady) return [];
    const out: T[] = [];
    for (const o of query.data) {
      if (filter && !filter(o)) continue;
      out.push(o);
    }
    return out.concat(injectElements);
  }, [query.isSuccess, query.data, filterReady, filter, injectElements]);

  const byId = useMemo(() => {
    const m = new Map<number, T>();
    for (const o of filteredOptions) {
      m.set(o[identityProp as keyof T] as unknown as number, o);
    }
    return m;
  }, [filteredOptions, identityProp]);

  const sortedOptions: T[] = useMemo(() => {
    const opts = [...filteredOptions];
    opts.sort((a, b) => {
      const aId = a[identityProp as keyof T] as unknown as number;
      const bId = b[identityProp as keyof T] as unknown as number;

      const fa = favorites.includes(aId);
      const fb = favorites.includes(bId);
      if (fa !== fb) return fa ? -1 : 1;
      return displayFunction(a).localeCompare(displayFunction(b));
    });
    return opts;
  }, [filteredOptions, favorites, displayFunction, identityProp]);

  const selectedObject: T | null = useMemo(() => {
    // If caller controls object, trust it.
    if (effectiveObjectProp !== undefined) return effectiveObjectProp ?? null;

    // Else derive from id.
    const id = effectiveIdProp ?? 0;
    if (!id) return null;
    return byId.get(id) ?? null;
  }, [effectiveObjectProp, effectiveIdProp, byId]);

  const selectedId: number = useMemo(() => {
    if (selectedObject) {
      return selectedObject[identityProp as keyof T] as unknown as number;
    }
    if (effectiveObjectProp !== undefined) return 0;
    return effectiveIdProp ?? 0;
  }, [selectedObject, effectiveObjectProp, effectiveIdProp, identityProp]);

  // sideEffect startup gating
  const startupCompleteRef = useRef(false);
  const startupCommitHappenedRef = useRef(false);

  const commitSelection = (obj: T | null, origin: string) => {
    console.log("commitSelection", obj, origin);

    const id = obj?.[identityProp as keyof T] !== undefined ? (obj[identityProp as keyof T] as unknown as number) : 0;

    // Mark that startup already emitted a semantic selection/change.
    // This prevents the separate startup effect from firing again with stale selectedObject.
    if (!startupCompleteRef.current) {
      startupCommitHappenedRef.current = true;
    }

    // New unified event
    onChange?.(obj, id, origin === "actual change" ? "user" : "automatic");

    // Legacy events
    setObject?.(obj);
    setId?.(id);

    // Legacy sideEffect behavior:
    // - if sideEffectOnlyOnChange is false, this fires on all semantic changes,
    //   including startup programmatic corrections
    // - if true, it only fires after startup is complete
    if (sideEffect) {
      if (!sideEffectOnlyOnChange || startupCompleteRef.current) {
        console.log("SIDEEFFECT1", obj);
        sideEffect(obj);
      }
    }
  };

  // Programmatic autocorrect/autoselect logic
  useEffect(() => {
    if (!query.isSuccess || !filterReady) return;

    const size = byId.size;

    if (size === 1) {
      const only = filteredOptions[0] ?? null;
      const onlyId = only?.[identityProp as keyof T] !== undefined ? (only[identityProp as keyof T] as unknown as number) : 0;

      if (only && selectedId !== onlyId) {
        commitSelection(only, "only");
      }
      return;
    }

    if (selectedId > 0 && !byId.has(selectedId)) {
      commitSelection(null, "na");
      return;
    }

    if (autoSelect && size > 1 && selectedId === 0) {
      const first = sortedOptions[0] ?? null;
      if (first) commitSelection(first, "first");
    }
  }, [query.isSuccess, filterReady, byId, filteredOptions, sortedOptions, autoSelect, selectedId, identityProp]);

  // Initial ready pass behavior for sideEffectOnlyOnChange === false
  useEffect(() => {
    const ready = query.isSuccess && filterReady;
    if (!ready) return;

    const firstReadyPass = !startupCompleteRef.current;
    if (!firstReadyPass) return;

    startupCompleteRef.current = true;

    // Old semantics: if sideEffectOnlyOnChange is false, run once on initial ready
    // But do not run again if startup already emitted a programmatic semantic change.
    if (!sideEffectOnlyOnChange && !startupCommitHappenedRef.current) {
      console.log("SIDEEFFECT2", selectedObject);
      sideEffect?.(selectedObject);
    }
  }, [query.isSuccess, filterReady, sideEffectOnlyOnChange, sideEffect, selectedObject]);

  if (!query.isSuccess) {
    return <Skeleton variant="text" sx={{ fontSize: "2rem" }} />;
  }

  if (!showWithoutChoice && byId.size === 1 && selectedId) {
    return null;
  }

  return wrapper(
    <Autocomplete<T, false, false, false>
      sx={{ mt: "10px", mb: "6px", ...(sx as any) }}
      value={selectedObject}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onChange={(_event, newValue) => {
        commitSelection(newValue ?? null, "actual change");
      }}
      clearIcon={clearIcon}
      size={size}
      fullWidth={fullWidth}
      options={showOptions ? sortedOptions : []}
      disabled={!!disabled}
      isOptionEqualToValue={(opt, val) => (opt[identityProp as keyof T] as unknown as number) === (val[identityProp as keyof T] as unknown as number)}
      getOptionLabel={(opt) => displayFunction(opt)}
      groupBy={
        showOptions
          ? (opt) => {
              const optId = opt[identityProp as keyof T] as unknown as number;
              return favorites.includes(optId) ? favoritesTitle : (displayFunction(opt)[0] ?? "").toUpperCase();
            }
          : undefined
      }
      noOptionsText={
        showAllOptions ? "Nichts gefunden..." : showOptions ? "Nichts gefunden..." : "Gib mindestens 3 Buchstaben in die Suchleiste ein."
      }
      renderInput={(params) => <TextField {...params} placeholder={placeholder ? placeholder : label} label={label} />}
    />,
  );
}
